import * as React from 'react';

import {
  FIELD_NAME_ENUM,
  isCatchallFieldName,
} from 'app/pages/TemplateGenerationSettingsComponent/data/constants';
import type { EmailTemplateSegmentConfig, FieldCondition } from 'app/pages/TemplateGenerationSettingsComponent/index';

type SegmentOption = {
  _id: string;
  name?: string;
  segment_name?: string;
};

type FieldValueByFieldName = Record<string, string[]>;
type FieldNamesBySegment = Record<string, string[]>;

const emptyConfig: EmailTemplateSegmentConfig = {
  segment_id: '',
  segment_name: '',
  allowed_message_types: [],
  auto_approval: false,
  field_conditions: [],
};

const emptyCondition: FieldCondition = { field_name: '', field_value: '' };

interface UseTemplateGenerationModalStateArgs {
  open: boolean;
  mode: 'add' | 'edit';
  initialValues?: EmailTemplateSegmentConfig | null;
  segments: SegmentOption[];
  segmentsLoading?: boolean;
  onSave: (config: EmailTemplateSegmentConfig) => void;
  fieldValueByFieldName?: FieldValueByFieldName;
  fieldNamesBySegment?: FieldNamesBySegment;
}

export function useTemplateGenerationModalState({
  open,
  mode,
  initialValues,
  segments,
  segmentsLoading: _segmentsLoading,
  onSave,
  fieldValueByFieldName = {},
  fieldNamesBySegment = {},
}: UseTemplateGenerationModalStateArgs) {
  // Currently not required for logic, but passed in by the page.
  void _segmentsLoading;
  const [form, setForm] = React.useState<EmailTemplateSegmentConfig>(emptyConfig);

  const [conditionErrors, setConditionErrors] = React.useState<
    Record<number, { fieldName?: string; fieldValue?: string }>
  >({});

  // Derive available field names for the current segment from dealer segment data.
  // Match by segment_name first, then segment_id. Fall back to full FIELD_NAME_ENUM if no match.
  const availableFieldNames = React.useMemo(() => {
    const key = form.segment_name?.trim() || form.segment_id?.trim();
    if (!key) return [...FIELD_NAME_ENUM];

    const fromDealer =
      fieldNamesBySegment[key] ??
      Object.entries(fieldNamesBySegment).find(
        ([k]) => k.trim().toLowerCase() === key.toLowerCase(),
      )?.[1];

    return fromDealer && fromDealer.length > 0 ? fromDealer : [...FIELD_NAME_ENUM];
  }, [form.segment_name, form.segment_id, fieldNamesBySegment]);

  // Reset/initialize form when modal opens.
  React.useEffect(() => {
    if (!open) return;

    setConditionErrors({});

    const initialConditions =
      mode === 'edit' && initialValues ? [...(initialValues.field_conditions || [])] : [];
    const normalizedConditions =
      initialConditions.length > 0 ? initialConditions : [{ ...emptyCondition }];

    if (mode === 'edit' && initialValues) {
      setForm({
        segment_id: initialValues.segment_id || '',
        segment_name: initialValues.segment_name || '',
        allowed_message_types: [...(initialValues.allowed_message_types || [])],
        auto_approval: Boolean(initialValues.auto_approval),
        field_conditions: normalizedConditions.map(c => ({
          field_name: (c.field_name ?? '').toString(),
          field_value: (c.field_value ?? '').toString(),
        })),
      });
      return;
    }

    setForm({
      ...emptyConfig,
      field_conditions: normalizedConditions.map(c => ({
        field_name: (c.field_name ?? '').toString(),
        field_value: (c.field_value ?? '').toString(),
      })),
    });
  }, [open, mode, initialValues]);

  // If available field names change due to segment updates, clear invalid field_name selections.
  React.useEffect(() => {
    setForm(prev => {
      const updated = (prev.field_conditions || []).map(cond => {
        const fn = cond.field_name ?? '';
        if (!fn) return cond;
        if (!availableFieldNames.includes(fn)) {
          return { ...cond, field_name: '', field_value: '' };
        }
        return cond;
      });
      return { ...prev, field_conditions: updated };
    });
  }, [availableFieldNames]);

  const segmentName = React.useCallback((s: SegmentOption) => s.name ?? s.segment_name ?? s._id, []);

  const handleSegmentChange = React.useCallback(
    (segmentId: string) => {
      const seg = segments.find(s => s._id === segmentId);
      setForm(prev => ({
        ...prev,
        segment_id: segmentId,
        segment_name: seg ? segmentName(seg) : '',
      }));
    },
    [segments, segmentName],
  );

  const handleSegmentIdChange = React.useCallback((value: string) => {
    setForm(prev => ({ ...prev, segment_id: value }));
  }, []);

  const handleSegmentNameChange = React.useCallback((value: string) => {
    setForm(prev => ({ ...prev, segment_name: value }));
  }, []);

  const handleMessageTypeChange = React.useCallback((type: string, checked: boolean) => {
    setForm(prev => {
      const arr = prev.allowed_message_types ? [...prev.allowed_message_types] : [];
      if (checked) {
        if (!arr.includes(type)) arr.push(type);
      } else {
        return { ...prev, allowed_message_types: arr.filter(t => t !== type) };
      }
      return { ...prev, allowed_message_types: arr };
    });
  }, []);

  const handleAutoApprovalChange = React.useCallback((checked: boolean) => {
    setForm(prev => ({ ...prev, auto_approval: checked }));
  }, []);

  const hasCatchallCondition = React.useMemo(
    () => (form.field_conditions || []).some(c => isCatchallFieldName(c.field_name)),
    [form.field_conditions],
  );

  /** CATCHALL means everything is included — no additional condition rows. */
  const canAddConditionRow = !hasCatchallCondition;

  const getFieldNameOptionsForRow = React.useCallback(
    (index: number) => {
      const conditions = form.field_conditions || [];
      const currentIsCatchall = isCatchallFieldName(conditions[index]?.field_name);
      const anySpecificSelected = conditions.some(
        c => Boolean((c.field_name ?? '').trim()) && !isCatchallFieldName(c.field_name),
      );

      return availableFieldNames.filter(opt => {
        if (!isCatchallFieldName(opt)) return true;
        // CATCHALL only when this row already has it, or no specific fields are selected.
        return currentIsCatchall || !anySpecificSelected;
      });
    },
    [availableFieldNames, form.field_conditions],
  );

  const handleConditionFieldNameChange = React.useCallback((index: number, value: string) => {
    if (isCatchallFieldName(value)) {
      setConditionErrors({});
      setForm(prev => ({
        ...prev,
        field_conditions: [{ field_name: value, field_value: '' }],
      }));
      return;
    }

    setConditionErrors(prevErrors => {
      const row = prevErrors[index];
      if (!row?.fieldName && !row?.fieldValue) return prevErrors;
      const next = { ...prevErrors };
      const { fieldName: _removed, ...rest } = row ?? {};
      if (!rest.fieldValue) {
        delete next[index];
      } else {
        next[index] = rest;
      }
      return next;
    });

    setForm(prev => ({
      ...prev,
      field_conditions: (prev.field_conditions || []).map((cond, i) =>
        i === index ? { ...cond, field_name: value, field_value: '' } : cond,
      ),
    }));
  }, []);

  const handleConditionFieldValueChange = React.useCallback(
    (index: number, value: string) => {
      setConditionErrors(prevErrors => {
        const row = prevErrors[index];
        if (!row?.fieldValue) return prevErrors;
        const next = { ...prevErrors };
        const { fieldValue: _removed, ...rest } = row;
        if (!rest.fieldName) {
          delete next[index];
        } else {
          next[index] = rest;
        }
        return next;
      });

      setForm(prev => ({
        ...prev,
        field_conditions: (prev.field_conditions || []).map((cond, i) =>
          i === index ? { ...cond, field_value: value } : cond,
        ),
      }));
    },
    [],
  );

  const handleAddConditionRow = React.useCallback(() => {
    setForm(prev => {
      if ((prev.field_conditions || []).some(c => isCatchallFieldName(c.field_name))) {
        return prev;
      }
      return {
        ...prev,
        field_conditions: [...(prev.field_conditions || []), { ...emptyCondition }],
      };
    });
  }, []);

  const handleRemoveCondition = React.useCallback((index: number) => {
    setConditionErrors({});
    setForm(prev => ({
      ...prev,
      field_conditions: (prev.field_conditions || []).filter((_, i) => i !== index),
    }));
  }, []);

  const getFieldValueOptions = React.useCallback(
    (fieldName: string) => {
      if (!fieldName) return [];
      const exact = fieldValueByFieldName[fieldName];
      if (exact) return exact;
      return (
        Object.entries(fieldValueByFieldName).find(
          ([k]) => k.trim().toLowerCase() === fieldName.trim().toLowerCase(),
        )?.[1] ?? []
      );
    },
    [fieldValueByFieldName],
  );

  const handleSubmit = React.useCallback(() => {
    const conditions = form.field_conditions || [];
    const errors: Record<number, { fieldName?: string; fieldValue?: string }> = {};

    conditions.forEach((c, index) => {
      const field_name = (c.field_name ?? '').trim();
      const field_value = (c.field_value ?? '').trim();
      const rowErrors: { fieldName?: string; fieldValue?: string } = {};

      if (!field_name) {
        rowErrors.fieldName = 'Field Name is required';
      } else if (!isCatchallFieldName(field_name) && !field_value) {
        rowErrors.fieldValue = 'Field Value is required';
      }

      if (rowErrors.fieldName || rowErrors.fieldValue) {
        errors[index] = rowErrors;
      }
    });

    if (Object.keys(errors).length > 0) {
      setConditionErrors(errors);
      return;
    }

    setConditionErrors({});

    // Keep complete rows. CATCHALL is a standalone field — no field_value required.
    // If CATCHALL is present, it alone is saved (covers everything).
    let sanitizedConditions: FieldCondition[] = conditions
      .map(c => {
        const field_name = (c.field_name ?? '').trim();
        const field_value = isCatchallFieldName(field_name)
          ? ''
          : (c.field_value ?? '').trim();
        return { field_name, field_value };
      })
      .filter(
        c =>
          Boolean(c.field_name) &&
          (isCatchallFieldName(c.field_name) || Boolean(c.field_value)),
      );

    if (sanitizedConditions.some(c => isCatchallFieldName(c.field_name))) {
      sanitizedConditions = [{ field_name: 'CATCHALL', field_value: '' }];
    }

    onSave({
      ...form,
      field_conditions: sanitizedConditions,
    });
  }, [form, onSave]);

  return {
    form,
    availableFieldNames,
    getFieldNameOptionsForRow,
    getFieldValueOptions,
    canAddConditionRow,
    conditionErrors,
    handleSegmentChange,
    handleSegmentIdChange,
    handleSegmentNameChange,
    handleMessageTypeChange,
    handleAutoApprovalChange,
    handleConditionFieldNameChange,
    handleConditionFieldValueChange,
    handleAddConditionRow,
    handleRemoveCondition,
    handleSubmit,
  };
}

